import { Box, Button } from '@chakra-ui/react';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import ReactExport from 'react-export-excel';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useState } from 'react';
import c from '../../constant';
import DisplayUserNameById from '../../components/DisplayUserNameById';
import useStore from '../../store';
import getDepartmentLabel from '../../utils/getDepartmentLabel';

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

const CATEGORY_LABELS = {
	en: {
		RequestForMeetingParticipation: 'Request for Meeting Participation (Phnom Penh)',
		Mission: 'Mission (Domestic and International)',
		AttendanceRequest: 'Attendance Request: Forgot to Check-in/Check-out',
	},
	kh: {
		RequestForMeetingParticipation: 'ការស្នើសុំចូលរួមកិច្ចប្រជុំ (នៅក្នុងភ្នំពេញ)',
		Mission: 'បេសកកម្ម (ក្នុងប្រទេស និងក្រៅប្រទេស)',
		AttendanceRequest: 'សំណើវត្តមាន៖ ភ្លេច ស្កេនចូល/ស្កេនចេញ',
	},
};

const toStr = (value) => String(value ?? '');

export default function ExportExcelMissionRecord({ records = [], ...props }) {
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const [users, setUsers] = useState({});
	const filename = lang === 'en' ? 'Mission_Records' : 'កំណត់ត្រាបេសកកម្ម';

	const userIds = useMemo(() => {
		return [...new Set(records?.map((item) => item?.createdBy).filter(Boolean))];
	}, [records]);

	const handleUserLoaded = useCallback((id, userInfo) => {
		setUsers((prev) => ({
			...prev,
			[id]: userInfo,
		}));
	}, []);

	function getCategoryLabel(category) {
		const translateCategory = lang === 'kh' ? 'kh' : 'en';
		return CATEGORY_LABELS?.[translateCategory]?.[category] || '';
	}

	const allUsersLoaded = userIds.length === 0 || userIds.every((id) => users[id]);

	const data = useMemo(() => {
		return (
			records?.flatMap((item) => {
				const participantViewers = item?.viewers?.filter((v) => v?.viewerType === 'PARTICIPANT') || [];

				if (!participantViewers?.length) {
					return [
						{
							no: toStr(item?.no),
							participant: '',
							category: toStr(getCategoryLabel(item?.category)),
							reason: toStr(item?.reason),
							amountOfDays: toStr(item?.amountOfDays),
							fromDate: item?.fromDate ? moment(item?.fromDate).format('YYYY-MM-DD HH:mm') : '',
							fromTime: item?.fromDate ? moment(item?.fromDate).format('HH:mm') : '',
							toDate: item?.toDate ? moment(item?.toDate).format('YYYY-MM-DD HH:mm') : '',
							toTime: item?.toDate ? moment(item?.toDate).format('HH:mm') : '',
							department: t(getDepartmentLabel(item?.viewers?.viewer?.department)),
							createdAt: item?.createdAt ? moment(item?.createdAt).format('YYYY-MM-DD HH:mm') : '',
							status: toStr(t(item?.status)),
						},
					];
				}

				return participantViewers?.map((participant) => ({
					no: toStr(item?.no),
					participant: toStr(lang === 'kh' ? participant?.viewer?.fullNameKhmer : participant?.viewer?.fullName),
					category: toStr(getCategoryLabel(item?.category)),
					reason: toStr(item?.reason),
					amountOfDays: toStr(item?.amountOfDays),
					fromDate: item?.fromDate ? moment(item?.fromDate).format('YYYY-MM-DD') : '',
					fromTime: item?.fromDate ? moment(item?.fromDate).format('HH:mm') : '',
					toDate: item?.toDate ? moment(item?.toDate).format('YYYY-MM-DD') : '',
					toTime: item?.toDate ? moment(item?.toDate).format('HH:mm') : '',
					department: t(getDepartmentLabel(participant.viewer?.department)),
					createdAt: item?.createdAt ? moment(item?.createdAt).format('YYYY-MM-DD HH:mm') : '',
					status: toStr(t(item?.status)),
				}));
			}) || []
		);
	}, [records, lang, t]);

	return (
		<Box {...props}>
			<Box display="none">
				{userIds.map((id) => (
					<DisplayUserNameById key={id} id={id} onLoaded={handleUserLoaded} />
				))}
			</Box>

			<ExcelFile
				filename={
					lang === 'en'
						? `${filename}_Generated_At_${moment().format(c.excelDateTimeFormat)}`
						: `${filename}_បង្កើតនៅថ្ងៃ_${moment().format(c.excelDateTimeFormat)}`
				}
				element={
					<Button
						type="button"
						leftIcon={<FontAwesomeIcon icon={faFileExcel} style={{ fontSize: 16 }} />}
						colorScheme="gray"
						variant="solid"
						size="sm"
						borderRadius={c.borderRadius}
						border="1px solid #bdc3c7"
						isDisabled={!records?.length || !allUsersLoaded}
					>
						{allUsersLoaded ? t('Export') : t('Loading')}
					</Button>
				}
			>
				<ExcelSheet data={data} name="Mission Records">
					<ExcelColumn label={t('Number')} value="no" />
					<ExcelColumn label={t('Participant')} value="participant" />
					<ExcelColumn label={t('Category')} value="category" />
					<ExcelColumn label={t('Reason')} value="reason" />
					<ExcelColumn label={t('Amount of Days')} value="amountOfDays" />
					<ExcelColumn label={t('Date From')} value="fromDate" />
					<ExcelColumn label={t('From Time')} value="fromTime" />
					<ExcelColumn label={t('Date To')} value="toDate" />
					<ExcelColumn label={t('To Time')} value="toTime" />
					<ExcelColumn label={t('Department')} value="department" />
					<ExcelColumn label={t('Created At')} value="createdAt" />
					<ExcelColumn label={t('Status')} value="status" />
				</ExcelSheet>
			</ExcelFile>
		</Box>
	);
}
